import type { CSSProperties } from 'react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { formatDistanceToNow, format, isValid, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { Application, EntraReason, Environment, Network } from '../types';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export function initials(name?: string | null, email?: string | null): string {
    if (name) {
        return name
            .split(' ')
            .filter(Boolean)
            .map((part) => part[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    }
    if (email) return email[0].toUpperCase();
    return '?';
}

export function formatDate(value: string | null | undefined): string {
    if (!value) return '-';
    const date = parseISO(value);
    return isValid(date) ? format(date, 'd MMMM yyyy', { locale: fr }) : '-';
}

export function formatDateTime(value: string | null | undefined): string {
    if (!value) return '-';
    const date = parseISO(value);
    return isValid(date) ? format(date, "d MMM yyyy 'à' HH:mm", { locale: fr }) : '-';
}

export function formatRelative(value: string | null | undefined): string {
    if (!value) return '-';
    const date = parseISO(value);
    if (!isValid(date)) return '-';
    return formatDistanceToNow(date, { addSuffix: true, locale: fr });
}

const ENVIRONMENT_LABELS: Record<Environment, string> = {
    prod: 'Production',
    staging: 'Recette',
    dev: 'Développement',
    local: 'Local',
};

const ENVIRONMENT_SHORT: Record<Environment, string> = {
    prod: 'Prod',
    staging: 'Recette',
    dev: 'Dev',
    local: 'Local',
};

const NETWORK_LABELS: Record<Network, string> = {
    public: 'Réseau public',
    private: 'Réseau interne',
};

export function environmentLabel(env: Environment, short = false): string {
    return short ? ENVIRONMENT_SHORT[env] : ENVIRONMENT_LABELS[env];
}

export function networkLabel(network: Network): string {
    return NETWORK_LABELS[network];
}

export const ACCESS_MODE_LABELS = {
    everyone: 'Tout le monde',
    user_list: 'Liste d’utilisateurs',
    entra_group: 'Groupe Entra ID',
} as const;

export const STATUS_LABELS = {
    active: 'En service',
    beta: 'Bêta',
    maintenance: 'Maintenance',
    deprecated: 'Obsolète',
} as const;

/**
 * Motifs de la synchronisation de l'annuaire Entra ID.
 *
 * Les codes viennent de l'API et sont volontairement stables ; la traduction
 * vit ici pour que la base n'ait pas à porter une langue.
 */
export const ENTRA_REASON_LABELS: Record<EntraReason, string> = {
    motif_conforme: 'Adresse conforme au motif de Clippit',
    exception_declaree: 'Admis par exception',
    sans_adresse: 'Compte sans adresse exploitable',
    exclusion_declaree: 'Écarté explicitement par la configuration',
    compte_invite: 'Compte invité (B2B)',
    domaine_hors_perimetre: 'Domaine hors périmètre',
    marqueur_technique: 'Compte d’administration ou de service',
    motif_non_conforme: 'Adresse hors du motif attendu',
    sans_fonction: 'Aucune fonction renseignée dans Entra ID',
    adresse_en_double: 'Adresse déjà portée par un autre compte',
};

export const ACCESS_SOURCE_LABELS = {
    open: 'Accès par défaut des collaborateurs',
    manual: 'Attribué par un administrateur',
    request: 'Demande approuvée',
    entra: 'Hérité d’un groupe Entra ID',
    import: 'Importé lors de l’initialisation',
} as const;

/**
 * Instance à ouvrir par défaut.
 *
 * On privilégie l'instance marquée principale, puis la production, puis la
 * première déclarée. Une application peut n'en avoir aucune : le catalogue
 * référence aussi des applications mobiles ou des services sans URL web.
 */
export function primaryInstance(application: Application) {
    return (
        application.instances.find((i) => i.isPrimary) ??
        application.instances.find((i) => i.environment === 'prod') ??
        application.instances[0] ??
        null
    );
}

/** Vrai si l'application possède au moins une instance dans cet environnement. */
export function hasEnvironment(application: Application, env: Environment): boolean {
    return application.instances.some((i) => i.environment === env);
}

export function hasNetwork(application: Application, network: Network): boolean {
    return application.instances.some((i) => i.network === network);
}

/**
 * Expose la couleur d'accent d'une application au CSS.
 *
 * La couleur vient de la base et n'est pas toujours lisible : le marine du logo
 * et quelques ardoises très foncées disparaissent sur les cartes du mode
 * sombre. Elle n'est donc jamais peinte directement depuis le JSX : elle est
 * passée en variable, et c'est `index.css` qui décide de l'encre réellement
 * appliquée, brute en clair et éclaircie en sombre. Voir `--app-ink`.
 */
export function appColor(couleur: string): CSSProperties {
    return { '--app-color': couleur } as CSSProperties;
}

/** Retire les accents pour une recherche insensible à la casse et aux diacritiques. */
export function normalize(value: string): string {
    return value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '');
}
